if (!window.Eurus.loadedScript.has('pagination.js')) {
  window.Eurus.loadedScript.add('pagination.js');

  /*
   * In-stock-first ordering for collection grids ("buffer model" + full-page fill).
   * Shopify paginates the collection in its normal sort order. As pages are loaded
   * we append the in-stock (buyable) products immediately and hold the out-of-stock
   * products in a buffer; the buffer is only flushed to the END of the grid once
   * every server page has been consumed. To keep each step looking full, a load
   * pulls successive server pages until it has revealed about one page worth of
   * in-stock products (data-per-page). Net effect: a catalog ALWAYS shows all
   * in-stock products before any out-of-stock one — with any sort or filter, at any
   * catalog size. Only the collection section opts in (it renders data-product-id /
   * data-available on each .grid-item); blog and search grids are untouched.
   */
  window.Eurus.stockOrder = window.Eurus.stockOrder || {
    seen: new Set(),
    buffer: [],
    target: 0,
    loading: false,
    epoch: 0, // bumped on every filter/sort re-render to cancel in-flight loads
    reset() { this.seen = new Set(); this.buffer = []; this.target = 0; this.loading = false; this.epoch++; }
  };

  // A grid opts into the in-stock-first ordering when its items expose data-available.
  function isStockGrid(grid) {
    return !!(grid && grid.querySelector('.grid-item[data-available]'));
  }

  function getSectionId() {
    const el = document.querySelector('[id^="btn-pagination-"]');
    return el ? el.id.replace('btn-pagination-', '') : null;
  }

  // Current "next page" URL from the live Load more button / infinite-scroll sentinel.
  function getNextUrl(sectionId) {
    const container = document.getElementById('btn-pagination-' + sectionId);
    const link = container && container.querySelector('a[href]');
    return link ? link.getAttribute('href') : null;
  }

  function gridHasServerNext() {
    return !!document.querySelector('[id^="btn-pagination-"] a[href]');
  }

  function fetchDoc(url) {
    return fetch(url)
      .then((response) => response.text())
      .then((text) => new DOMParser().parseFromString(text, 'text/html'));
  }

  function renderButton(doc, sectionId) {
    const destination = document.getElementById('btn-pagination-' + sectionId);
    const source = doc.getElementById('btn-pagination-' + sectionId);
    if (destination && source) destination.innerHTML = source.innerHTML;
  }

  function flushBuffer() {
    const grid = document.getElementById('items-grid');
    const state = window.Eurus.stockOrder;
    if (grid && state.buffer.length) {
      grid.insertAdjacentHTML('beforeend', state.buffer.join(''));
      state.buffer = [];
    }
  }

  // Keep the "Showing X of Y" counter truthful: X must be the number of products
  // actually rendered, not Shopify's per-page server offset.
  function updateShowingCount() {
    const grid = document.getElementById('items-grid');
    if (!isStockGrid(grid)) return;
    const container = document.querySelector('[id^="btn-pagination-"]');
    const counter = container && container.querySelector('p');
    if (!counter) return;
    const visible = grid.querySelectorAll('.grid-item').length;
    counter.textContent = counter.textContent.replace(/\d[\d.,]*/, String(visible));
  }

  // Classify one fetched page: show in-stock immediately, hold out-of-stock in the
  // buffer. Returns { added, exhausted, nextUrl }.
  function absorbStockPage(doc, sectionId) {
    const grid = document.getElementById('items-grid');
    const state = window.Eurus.stockOrder;
    const fetchedGrid = doc.getElementById('items-grid');
    const items = fetchedGrid ? Array.from(fetchedGrid.querySelectorAll('.grid-item[data-product-id]')) : [];
    let added = 0;
    items.forEach((item) => {
      const id = item.getAttribute('data-product-id');
      if (id && state.seen.has(id)) return; // de-dupe (variant-level availability overlap)
      if (id) state.seen.add(id);
      if (item.getAttribute('data-available') === 'true') {
        grid.insertAdjacentHTML('beforeend', item.outerHTML);
        added++;
      } else {
        state.buffer.push(item.outerHTML);
      }
    });
    const source = doc.getElementById('btn-pagination-' + sectionId);
    const nextLink = source && source.querySelector('a[href]');
    return { added, exhausted: !nextLink, nextUrl: nextLink ? nextLink.getAttribute('href') : null };
  }

  // Reveal about `count` more in-stock products (one "page"), pulling successive
  // server pages as needed. Out-of-stock products go to the buffer and are revealed
  // only once every server page is consumed.
  async function advance(count, sectionId) {
    const state = window.Eurus.stockOrder;
    if (state.loading || !sectionId) return;
    state.loading = true;
    const epoch = state.epoch; // snapshot; a filter/sort re-render bumps this
    try {
      let added = 0;
      let guard = 0;
      let lastDoc = null;
      let url = getNextUrl(sectionId);
      // Fetch until we've shown a full page of in-stock, or the server runs out.
      // Bounded per interaction so sparse in-stock can't freeze the browser.
      while (added < count && url && guard < 60) {
        guard++;
        const doc = await fetchDoc(url);
        if (epoch !== state.epoch) return; // superseded by a filter/sort re-render
        lastDoc = doc;
        const result = absorbStockPage(lastDoc, sectionId);
        added += result.added;
        url = result.exhausted ? null : result.nextUrl;
      }
      if (epoch !== state.epoch) return;
      if (lastDoc) renderButton(lastDoc, sectionId);
      // url === null means every server page was consumed -> reveal out-of-stock.
      if (!url) flushBuffer();
      updateShowingCount();
    } catch (e) {
      console.error(e);
    } finally {
      // Only release the lock if we still own the current epoch; a superseding
      // re-render already reset state.loading for its own load.
      if (epoch === state.epoch) state.loading = false;
    }
  }

  // On first paint of a multi-page collection, move the first page's out-of-stock
  // products into the buffer, then top the first screen up to a full page of in-stock.
  function initStockGrid() {
    const grid = document.getElementById('items-grid');
    if (!isStockGrid(grid)) return;
    const state = window.Eurus.stockOrder;
    const perPage = parseInt(grid.getAttribute('data-per-page'), 10);
    state.target = (perPage && perPage > 0) ? perPage : grid.querySelectorAll('.grid-item').length;
    const hasNext = gridHasServerNext();
    Array.from(grid.querySelectorAll('.grid-item[data-product-id]')).forEach((item) => {
      const id = item.getAttribute('data-product-id');
      if (id) state.seen.add(id);
      if (hasNext && item.getAttribute('data-available') !== 'true') {
        state.buffer.push(item.outerHTML);
        item.remove();
      }
    });
    // Single page: nothing more to load; CSS order:99 already keeps OOS last.
    if (!hasNext) { updateShowingCount(); return; }
    const shown = grid.querySelectorAll('.grid-item').length;
    if (shown < state.target) {
      advance(state.target - shown, getSectionId());
    } else {
      updateShowingCount();
    }
  }
  // Exposed so facets.js can re-run it after a filter/sort re-render.
  window.Eurus.initStockGrid = initStockGrid;

  requestAnimationFrame(() => {
    document.addEventListener("alpine:init", () => {
      Alpine.data("xPagination", (sectionId) => ({
        loading: false,
        async loadData(url) {
          const grid = document.getElementById('items-grid');
          const blogGrid = document.getElementById('blog-grid');
          // Blog / search grids keep the original append behavior untouched.
          if (blogGrid || !isStockGrid(grid)) {
            this.loading = true;
            try {
              const doc = await fetchDoc(url);
              this._appendLegacy(doc, blogGrid || grid);
            } finally {
              this.loading = false;
            }
            return;
          }
          // Collection grid: reveal one more full page of in-stock products.
          if (window.Eurus.stockOrder.loading) return;
          this.loading = true;
          try {
            await advance(window.Eurus.stockOrder.target || 16, sectionId);
          } finally {
            this.loading = false;
          }
        },
        _appendLegacy(doc, dest) {
          const fetchedGrid = doc.getElementById('items-grid');
          const newProducts = fetchedGrid ? fetchedGrid.getElementsByClassName('grid-item') : [];
          if (!newProducts.length) { this._renderButton(doc); return; }
          for (let i = 0; i < newProducts.length; i++) {
            setTimeout(() => {
              dest.insertAdjacentHTML('beforeend', newProducts[i].innerHTML);
              if (i === newProducts.length - 1) {
                this._renderButton(doc);
              }
            }, i * 300);
          }
        },
        _renderButton(doc) {
          renderButton(doc, sectionId);
        }
      }));
    });
  });

  // Classify the initial server-rendered page as soon as the DOM is ready.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initStockGrid);
  } else {
    initStockGrid();
  }
}
